import { validateLocale } from "utils/validateLocale";
import { contentfulClient as client } from "../../contentful";
import type { BuilderContent } from "@builder.io/sdk";
import { builder } from "@builder.io/sdk";
import type { Locale } from "i18n/constants";
import type { EntryCollection } from "contentful";

export const BUILDER_LOCALES = [
  "en-US",
  "es-LA",
  "fr-FR",
  "it-IT",
  "pt-BR"
] as const;

export type BuilderLocale = (typeof BUILDER_LOCALES)[number];

export const CONTENTFUL_LOCALES = [
  "en-US",
  "en-SG",
  "es-LA",
  "fr-FR",
  "it-IT",
  "pt-BR"
] as const;

export type ContentfulLocale = (typeof CONTENTFUL_LOCALES)[number];

export const LOCALE_TO_BUILDER_LOCALES = {
  "en-US": "en-US",
  "en-SG": "en-US",
  "en-GB": "en-US",
  "en-AU": "en-US",
  es: "es-LA",
  fr: "fr-FR",
  it: "it-IT",
  pt: "pt-BR"
} as const satisfies Record<Locale, BuilderLocale>;

export const LOCALE_TO_CONTENTFUL_LOCALES = {
  "en-SG": "en-SG",
  "en-US": "en-US",
  "en-GB": "en-US",
  "en-AU": "en-US",
  es: "es-LA",
  fr: "fr-FR",
  it: "it-IT",
  pt: "pt-BR"
} as const satisfies Record<Locale, ContentfulLocale>;

export const DynamicApis = {
  Contentful: {
    fetchDataBySlug: async (locale?: Locale, slug?: string): Promise<any> => {
      const results = await client.getEntries({
        content_type: "pageTemplate",
        locale: validateLocale(locale),
        include: 5,
        "fields.slug[in]": slug
      });
      return results;
    },
    fetchAllPageTemplates: async (
      locale?: Locale
    ): Promise<EntryCollection<any>> => {
      const results = await client.getEntries({
        content_type: "pageTemplate",
        locale: validateLocale(locale),
        include: 5
      });
      return results;
    },
    fetchLatestBlog: async (
      locale?: Locale,
      blogTypes?: string[]
    ): Promise<any> => {
      const results = await client.getEntries({
        content_type: "blogPost",
        locale: validateLocale(locale),
        include: 5,
        limit: 3,
        order: "-sys.createdAt",
        ...(blogTypes?.length
          ? { "fields.blogType.sys.id[all]": (blogTypes || []).toString() }
          : {})
      });
      return results;
    }
  },
  Builder: {
    fetchBuilderPages: async (locale?: Locale): Promise<BuilderContent[]> => {
      const limit = 100;
      let offset = 0;
      const allResults: BuilderContent[] = [];
      const targetLocale = LOCALE_TO_BUILDER_LOCALES[locale ?? "en-US"];

      while (true) {
        const results = (await builder.getAll("page", {
          apiKey: process.env.NEXT_PUBLIC_BUILDER_PUBLIC_API_KEY,
          fields: "data.url",
          options: { noTargeting: true },
          locale: targetLocale,
          limit,
          offset
        })) as BuilderContent[];

        allResults.push(...results);

        if (results.length < limit) {
          break;
        }
        offset += limit;
      }

      return allResults;
    },
    fetchBuilderPageBySlug: async (slug: string, locale?: Locale) => {
      return builder
        .get("page", {
          apiKey: process.env.NEXT_PUBLIC_BUILDER_PUBLIC_API_KEY,
          options: { locale: LOCALE_TO_BUILDER_LOCALES[locale ?? "en-US"] },
          url: `/${slug}`,
          userAttributes: {
            locale: LOCALE_TO_BUILDER_LOCALES[locale ?? "en-US"],
            urlPath: `/${slug}`
          }
        })
        .toPromise();
    }
  }
};
